import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { withTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import axios from "../../components/Axios";
import WideContainer from "../../components/WideContainer";
import { characters } from "../../slices/characters";
import {
  DELETED,
  isOther,
  memoize,
  isUnnamed,
  hasSimilarity,
  rangeIds,
  filterCharacters,
  sortCharacters,
  ownerMap,
  groupByOwner,
  selectionSources,
} from "./tracks/tracksModel";
import TracksToolbar from "./tracks/TracksToolbar";
import SelectionBar from "./tracks/SelectionBar";
import MoveToMenu from "./tracks/MoveToMenu";
import CharacterRow from "./tracks/CharacterRow";
import "./tracks/TracksTab.css";

const TOAST_MS = 8000;
const NO_SEL = new Set();

/**
 * Tracks tab: every character on one line with its face tracks. Select tracks (click, shift-click,
 * drag across), then move them to another character (Move To, or drag onto a row), make a new
 * character from them, or delete them; rename characters and actors in place. Changes show at once
 * and are replaced by the server's character list; moves and deletes can be undone.
 */
class Index extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sel: NO_SEL, // selected track ids
      anchor: null, // { charId, trackId } for shift-click ranges
      expanded: new Set(), // char ids showing all their tracks
      edit: null, // { charId, name, actor, focus, suggestions, loadingNames }
      picker: null, // { mode: "move" | "merge", mergeFrom }
      menu: null, // { el, charId } row menu
      confirm: null, // char id about to be deleted
      dropTarget: null,
      dragging: null, // Set of track ids being dragged
      toast: null, // { key, text, undo: { groups, before } | null }
      q: "",
      sort: "screen",
      order: "time",
      pinned: [], // characters created here stay on top
      busy: false,
      moveBtn: null, // Move To button in the selection bar, anchors the picker
    };
    this.sweep = null; // drag-across selection in progress
    this.downOn = null; // track pressed, so its click doesn't toggle it back
    this.dragIds = null;

    this.getOwners = memoize(ownerMap);
    this.getHasSim = memoize(hasSimilarity);
    this.getList = memoize((items, q, sort, pinned) => sortCharacters(filterCharacters(items, q), sort, pinned));
    // Others isn't offered as a destination: moving there is what Delete does
    this.getPickList = memoize((items, sort, pinned) => sortCharacters(items.filter((c) => !isOther(c)), sort, pinned));

    // handlers handed to rows and tiles; stable so memoized tiles don't re-render
    this.h = {
      tileDown: this.tileDown.bind(this),
      tileEnter: this.tileEnter.bind(this),
      tileClick: this.tileClick.bind(this),
      toggleAll: this.toggleAll.bind(this),
      moveOne: this.moveOne.bind(this),
      deleteOne: (id) => this.move([id], DELETED),
      dragStart: this.dragStart.bind(this),
      dragEnd: this.dragEnd.bind(this),
      dragOver: this.dragOver.bind(this),
      dragLeave: this.dragLeave.bind(this),
      drop: this.drop.bind(this),
      expand: (charId) => this.setExpanded(charId, true),
      collapse: (charId) => this.setExpanded(charId, false),
      startEdit: (charId, focus) => this.startEdit(charId, null, focus),
      editField: (k, v) => this.setState((s) => (s.edit ? { edit: { ...s.edit, [k]: v } } : null)),
      saveEdit: this.saveEdit.bind(this),
      cancelEdit: () => this.setState({ edit: null }),
      openMenu: (el, charId) => this.setState({ menu: { el, charId }, picker: null }),
    };
    this.setMoveBtn = (el) => el !== this.state.moveBtn && this.setState({ moveBtn: el });
    this.endSweep = () => (this.sweep = null);
  }

  componentDidMount() {
    window.addEventListener("pointerup", this.endSweep);
    window.addEventListener("pointercancel", this.endSweep);
  }

  componentWillUnmount() {
    window.removeEventListener("pointerup", this.endSweep);
    window.removeEventListener("pointercancel", this.endSweep);
  }

  // ---------------------------------------------------------------- helpers
  api(path) {
    return "/video_project/" + this.props.project.project_id + "/characters" + (path || "");
  }
  items() {
    return this.props.characters.items;
  }
  find(charId) {
    return this.items().find((c) => c.char_id === charId);
  }
  user() {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch (e) {
      return {};
    }
  }
  // thumbnails live under the user's folder on the CDN
  base() {
    return (this.user().user_folder || "") + "/" + this.props.project.project_folder;
  }
  // suggest_names wants the S3 key of a face crop
  thumbKey(trackId) {
    return String(this.user().user_folder || "").split("/")[3] + "/" + this.props.project.project_folder + "/tracks_thumbnails/" + trackId + ".jpg";
  }
  plural(count, one, many, opts) {
    const { t } = this.props;
    return count === 1 ? t(one, opts) : t(many, { ...opts, count });
  }

  // replace the list with the server's; on failure put back `rollback` (the Axios interceptor reports the error)
  request(method, path, body, rollback) {
    return axios[method](this.api(path), body)
      .then((r) => {
        this.props.dispatch(characters.actions.setItems(r.data));
        return r.data;
      })
      .catch((e) => {
        console.log(e);
        if (rollback) this.props.dispatch(characters.actions.setItems(rollback));
        return null;
      });
  }

  // a character's face embedding follows its tracks; recompute for every character a move touched
  refreshEmbeddings(charIds) {
    const ids = Array.from(new Set(charIds)).filter((id) => id !== DELETED && id !== undefined && id !== null);
    if (!ids.length) return;
    axios
      .post("/inference", { path: "/characters/update_embeddings", project_folder: this.props.project.project_folder, char_ids: ids })
      .catch((e) => console.log(e));
  }

  toast(text, undo) {
    this.setState({ toast: { key: Date.now(), text, undo: undo || null } });
  }

  // ---------------------------------------------------------------- selection
  // press an unselected track and drag across its neighbours to select them; shift-click selects a range
  tileDown(e, charId, ids, index) {
    if (e.button !== 0 || e.pointerType === "touch") return;
    const id = ids[index];
    const { sel, anchor } = this.state;
    if (sel.has(id) && !e.shiftKey) return; // pressing a selected track may start a drag
    this.downOn = id;
    const next = new Set(sel);
    if (e.shiftKey && anchor && anchor.charId === charId) {
      rangeIds(ids, anchor.trackId, id).forEach((x) => next.add(x));
      this.setState({ sel: next, picker: null });
      return;
    }
    this.sweep = { charId, ids, start: index, base: sel };
    next.add(id);
    this.setState({ sel: next, anchor: { charId, trackId: id }, picker: null });
  }

  tileEnter(e, charId, index) {
    const sw = this.sweep;
    if (!sw || sw.charId !== charId) return;
    if (!(e.buttons & 1)) {
      this.sweep = null;
      return;
    }
    const next = new Set(sw.base);
    rangeIds(sw.ids, sw.ids[sw.start], sw.ids[index]).forEach((x) => next.add(x));
    this.setState({ sel: next });
  }

  // keyboard, or a click on an already selected track: toggle it
  tileClick(e, charId, id) {
    if (this.downOn === id && e.detail !== 0) {
      this.downOn = null;
      return;
    }
    this.downOn = null;
    const next = new Set(this.state.sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.setState({ sel: next, anchor: { charId, trackId: id }, picker: null });
  }

  toggleAll(ids) {
    const next = new Set(this.state.sel);
    const all = ids.every((id) => next.has(id));
    ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
    this.setState({ sel: next, picker: null });
  }

  clearSelection() {
    this.setState({ sel: NO_SEL, anchor: null, picker: null });
  }

  setExpanded(charId, on) {
    const next = new Set(this.state.expanded);
    if (on) next.add(charId);
    else next.delete(charId);
    this.setState({ expanded: next });
  }

  // hover button on a track: select just it and open Move To
  moveOne(charId, id) {
    this.setState({ sel: new Set([id]), anchor: { charId, trackId: id }, picker: { mode: "move" }, menu: null });
  }

  // ---------------------------------------------------------------- drag a selection onto a character
  dragStart(e, charId) {
    const ids = Array.from(this.state.sel);
    if (this.sweep || !ids.length) {
      e.preventDefault();
      return;
    }
    try {
      e.dataTransfer.setData("text/plain", ids.join(","));
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {}
    this.dragIds = ids;
    this.setState({ dragging: new Set(ids), picker: null, menu: null });
  }

  dragEnd() {
    this.dragIds = null;
    if (this.state.dragging || this.state.dropTarget !== null) this.setState({ dragging: null, dropTarget: null });
  }

  dragOver(e, charId) {
    if (!this.dragIds) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (this.state.dropTarget !== charId) this.setState({ dropTarget: charId });
  }

  dragLeave(e, charId) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (this.state.dropTarget === charId) this.setState({ dropTarget: null });
  }

  drop(e, charId) {
    if (!this.dragIds) return;
    e.preventDefault();
    const ids = this.dragIds;
    this.dragEnd();
    this.move(ids, charId);
  }

  // ---------------------------------------------------------------- changes
  /** move tracks to a character (DELETED removes them); shows at once, can be undone */
  move(ids, toCharId, opts = {}) {
    const before = this.items();
    const groups = groupByOwner(ids, this.getOwners(before), toCharId);
    const moving = groups.reduce((all, g) => all.concat(g.track_ids), []);
    this.setState({ sel: NO_SEL, anchor: null, picker: null, menu: null });
    if (!moving.length) return Promise.resolve(false);

    this.props.dispatch(characters.actions.moveTracksLocal({ trackIds: moving, toCharId }));
    return this.request("post", "/move_tracks", { char_id: toCharId, track_ids: moving }, before).then((data) => {
      if (!data) return false;
      this.refreshEmbeddings([toCharId].concat(groups.map((g) => g.char_id)));
      if (!opts.quiet) {
        const to = before.find((c) => c.char_id === toCharId);
        const text =
          opts.text ||
          (toCharId === DELETED
            ? this.plural(moving.length, "1 track deleted", "{{count}} tracks deleted")
            : this.plural(moving.length, "1 track moved to {{name}}", "{{count}} tracks moved to {{name}}", { name: to ? to.char_name : "" }));
        this.toast(text, { groups, before, to: toCharId });
      }
      return true;
    });
  }

  /** put tracks back with their previous characters: one move_tracks per character */
  undo() {
    const { toast } = this.state;
    if (!toast || !toast.undo) return;
    const { groups, before, to } = toast.undo;
    const rollback = this.items();
    this.setState({ toast: null });
    this.props.dispatch(characters.actions.setItems(before));
    groups
      .reduce(
        (p, g) => p.then((last) => last && this.request("post", "/move_tracks", { char_id: g.char_id, track_ids: g.track_ids }, last)),
        Promise.resolve(rollback)
      )
      .then((ok) => ok && this.refreshEmbeddings([to].concat(groups.map((g) => g.char_id))));
  }

  /** create a character, move `ids` into it (if any) and open it for naming */
  newCharacter(ids) {
    const known = new Set(this.items().map((c) => c.char_id));
    this.setState({ busy: true, picker: null });
    return axios
      .post(this.api(""), {})
      .then((r) => {
        this.props.dispatch(characters.actions.setItems(r.data));
        const created = r.data.find((c) => !known.has(c.char_id));
        this.setState((s) => ({ busy: false, pinned: created ? [created.char_id].concat(s.pinned) : s.pinned }));
        if (!created) return;
        this.startEdit(created.char_id, created);
        if (ids && ids.length)
          this.move(ids, created.char_id, {
            text: this.plural(ids.length, "1 track moved to a new character", "{{count}} tracks moved to a new character"),
          });
      })
      .catch((e) => {
        console.log(e);
        this.setState({ busy: false });
      });
  }

  /** `focus` picks the field that gets the cursor: "name" (default) or "actor" */
  startEdit(charId, known, focus = "name") {
    const c = known || this.find(charId);
    if (!c || isOther(c)) return;
    const hasFace = c.rep_track_id !== -1 && c.rep_track_id !== null && c.rep_track_id !== undefined;
    this.setState({
      edit: { charId, name: isUnnamed(c.char_name) ? "" : c.char_name || "", actor: c.char_actor || "", focus, suggestions: [], loadingNames: hasFace },
      menu: null,
    });
    if (!hasFace) return;
    const done = (suggestions) =>
      this.setState((s) => (s.edit && s.edit.charId === charId ? { edit: { ...s.edit, suggestions, loadingNames: false } } : null));
    axios
      .post("/characters/suggest_names", { path: this.thumbKey(c.rep_track_id) })
      .then((r) => done(Array.isArray(r.data) ? r.data : []))
      .catch(() => done([]));
  }

  saveEdit() {
    const { edit } = this.state;
    if (!edit) return;
    this.setState({ edit: null });
    const c = this.find(edit.charId);
    if (!c) return;
    const name = edit.name.trim() || c.char_name;
    const actor = edit.actor.trim();
    if (name === c.char_name && actor === (c.char_actor || "")) return;
    const before = this.items();
    this.props.dispatch(characters.actions.renameLocal({ charId: c.char_id, name, actor }));
    this.request("post", "/change_name", { char_id: c.char_id, name, actor }, before);
  }

  /** every track of `fromId` goes to `toId`, then `fromId` is deleted (not undoable) */
  merge(fromId, toId) {
    const from = this.find(fromId),
      to = this.find(toId);
    if (!from || !to) return;
    this.move(
      from.tracks_data.map((tr) => tr.track_id),
      toId,
      { quiet: true }
    ).then((ok) => {
      if (!ok) return;
      this.request("delete", "/" + fromId).then((data) => {
        if (data) this.toast(this.props.t("{{from}} merged into {{to}}", { from: from.char_name, to: to.char_name }));
      });
    });
  }

  deleteCharacter(charId) {
    const c = this.find(charId);
    this.setState({ confirm: null, sel: NO_SEL, picker: null, edit: null });
    if (!c) return;
    this.request("delete", "/" + charId).then((data) => {
      if (data) this.toast(this.props.t("{{name}} deleted", { name: c.char_name }));
    });
  }

  pick(charId) {
    const { picker, sel } = this.state;
    if (picker && picker.mode === "merge") this.merge(picker.mergeFrom, charId);
    else this.move(Array.from(sel), charId);
  }

  rowMenu(action) {
    const { menu } = this.state;
    if (!menu) return;
    const c = this.find(menu.charId);
    this.setState({ menu: null });
    if (!c) return;
    const ids = c.tracks_data.map((tr) => tr.track_id);
    if (action === "rename") this.startEdit(c.char_id);
    else if (action === "select") this.setState({ sel: new Set(ids), anchor: null, picker: null });
    else if (action === "merge") this.setState({ sel: new Set(ids), anchor: null, picker: { mode: "merge", mergeFrom: c.char_id } });
    else if (action === "delete") this.setState({ confirm: c.char_id });
  }

  // ---------------------------------------------------------------- render
  render() {
    const { t, characters: store } = this.props;
    const { sel, expanded, edit, picker, menu, confirm, dropTarget, dragging, toast, q, sort, pinned, busy } = this.state;
    const items = store.items;
    const owners = this.getOwners(items);
    const showSim = this.getHasSim(items);
    const order = showSim ? this.state.order : "time";
    const list = this.getList(items, q, sort, pinned);
    const base = this.base();

    const cast = items.filter((c) => !isOther(c)); // the Others bucket isn't counted
    const total = cast.reduce((n, c) => n + c.tracks_data.length, 0);
    const unnamed = cast.filter((c) => isUnnamed(c.char_name)).length;
    // named and unnamed apart: the other tabs count the named characters, the faces nobody named are extra here
    const stats = [
      unnamed ? t("{{count}} named", { count: cast.length - unnamed }) : this.plural(cast.length, "1 character", "{{count}} characters"),
      unnamed ? t("{{count}} unnamed", { count: unnamed }) : null,
      this.plural(total, "1 track", "{{count}} tracks"),
    ]
      .filter(Boolean)
      .join(" · ");

    const sources = sel.size ? selectionSources(sel, owners) : [];
    const merging = !!(picker && picker.mode === "merge");
    const mergeFrom = merging ? this.find(picker.mergeFrom) : null;
    const confirmChar = confirm !== null ? this.find(confirm) : null;
    const menuChar = menu ? this.find(menu.charId) : null;
    const otherMenu = isOther(menuChar); // the Others row can only select its tracks
    const other = items.find(isOther);

    return (
      <div className="tracksTab">
        <WideContainer>
          <div className="tk-page">
            <div className="tk-sticky">
              {sel.size ? (
                <SelectionBar
                  countText={this.plural(sel.size, "1 track selected", "{{count}} tracks selected")}
                  fromText={
                    sources.length === 1
                      ? t("from {{name}}", { name: (this.find(sources[0]) || {}).char_name || "" })
                      : t("from {{count}} characters", { count: sources.length })
                  }
                  merging={merging}
                  canDelete={!sources.every((id) => id === DELETED)}
                  moveRef={this.setMoveBtn}
                  onClear={() => this.clearSelection()}
                  onDelete={() => this.move(Array.from(sel), DELETED)}
                  onNew={() => this.newCharacter(Array.from(sel))}
                  onMove={() => this.setState({ picker: picker ? null : { mode: "move" } })}
                  t={t}
                />
              ) : (
                <TracksToolbar
                  stats={stats}
                  q={q}
                  sort={sort}
                  busy={busy}
                  onQuery={(v) => this.setState({ q: v })}
                  onSort={(v) => this.setState({ sort: v })}
                  onAdd={() => this.newCharacter([])}
                  t={t}
                />
              )}
            </div>

            {store.loading === "pending" ? (
              <CircularProgress className="tk-loading" />
            ) : (
              <div className={"tk-card" + (sel.size ? " is-selecting" : "")}>
                <div className="tk-head">
                  <div className="tk-headIdent">{t("Character")}</div>
                  <div className="tk-headTracks">
                    <span>{t("Tracks")}</span>
                    {showSim && (
                      <div className="tk-segs" role="group" aria-label={t("Track order")}>
                        {[
                          ["time", "By time"],
                          ["similar", "Least similar first"],
                        ].map(([k, label]) => (
                          <button key={k} type="button" className="tk-seg" aria-pressed={order === k} onClick={() => this.setState({ order: k, anchor: null })}>
                            {t(label)}
                          </button>
                        ))}
                      </div>
                    )}
                    <span className="tk-hint">{t("Drag across tracks to select · Shift-click for a range · Drag a selection onto a character to move it")}</span>
                  </div>
                  <div className="tk-headMenu" />
                </div>
                {list.map((c) => (
                  <CharacterRow
                    key={c.char_id}
                    c={c}
                    order={order}
                    base={base}
                    sel={sel}
                    dragging={dragging}
                    expanded={expanded.has(c.char_id)}
                    edit={edit && edit.charId === c.char_id ? edit : null}
                    showMatch={order === "similar"}
                    isDrop={dropTarget === c.char_id}
                    menuOpen={!!menu && menu.charId === c.char_id}
                    h={this.h}
                    t={t}
                  />
                ))}
                {!list.length && (
                  <div className="tk-noRows">
                    {q ? (
                      <>
                        <b>{t("No characters match “{{q}}”", { q })}</b>
                        {t("Try a character or actor name.")}
                      </>
                    ) : (
                      t("No characters yet.")
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </WideContainer>

        <MoveToMenu
          open={!!picker && sel.size > 0 && !!this.state.moveBtn}
          anchorEl={this.state.moveBtn}
          title={
            merging && mergeFrom
              ? t("Merge {{name}} into", { name: mergeFrom.char_name })
              : this.plural(sel.size, "Move 1 track to", "Move {{count}} tracks to")
          }
          items={this.getPickList(items, sort, pinned)}
          current={merging ? [picker.mergeFrom] : sources.length === 1 ? sources : []}
          showNew={!merging}
          base={base}
          onPick={(charId) => this.pick(charId)}
          onNew={() => this.newCharacter(Array.from(sel))}
          onClose={() => this.setState({ picker: null })}
          t={t}
        />

        <Menu
          className="tk-menu"
          anchorEl={menu ? menu.el : null}
          open={!!menuChar}
          onClose={() => this.setState({ menu: null })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {!otherMenu && <MenuItem onClick={() => this.rowMenu("rename")}>{t("Rename")}</MenuItem>}
          <MenuItem disabled={!menuChar || !menuChar.tracks_data.length} onClick={() => this.rowMenu("select")}>
            {t("Select all tracks")}
          </MenuItem>
          {!otherMenu && (
            <MenuItem disabled={!menuChar || !menuChar.tracks_data.length} onClick={() => this.rowMenu("merge")}>
              {t("Merge into…")}
            </MenuItem>
          )}
          {!otherMenu && <Divider />}
          {!otherMenu && <MenuItem onClick={() => this.rowMenu("delete")}>{t("Delete character")}</MenuItem>}
        </Menu>

        <Dialog className="lib-dialog" open={!!confirmChar} onClose={() => this.setState({ confirm: null })} maxWidth="xs" fullWidth>
          <DialogTitle>{t("Delete {{name}}?", { name: confirmChar ? confirmChar.char_name : "" })}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {other ? t("Its tracks will move to {{name}}. This can't be undone.", { name: other.char_name }) : t("This can't be undone.")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ confirm: null })}>{t("Cancel")}</Button>
            <Button color="error" onClick={() => this.deleteCharacter(confirm)}>
              {t("Delete")}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          key={toast ? toast.key : 0}
          open={!!toast}
          autoHideDuration={TOAST_MS}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={(e, reason) => reason !== "clickaway" && this.setState({ toast: null })}
        >
          <Alert
            variant="filled"
            severity="success"
            onClose={() => this.setState({ toast: null })}
            action={
              toast && toast.undo ? (
                <Button color="inherit" size="small" onClick={() => this.undo()}>
                  {t("Undo")}
                </Button>
              ) : undefined
            }
          >
            {toast ? toast.text : ""}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

const mapState = (state) => ({ characters: state.characters });
export default withTranslation()(withRouter(connect(mapState)(Index)));
